import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import PageTitle from "../../components/PageTitle";
import swal from "../../helpers/swal";
import is_money from "../../helpers/is_money";
import {
    HostelContainer,
    CurrentBookingCard,
    BookingHeader,
    BookingDetails,
    HostelsGrid,
    HostelCard,
    HostelImage,
    HostelInfo,
    HostelMeta,
    HostelPrice,
    SelectButton,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    RoomCard,
    BedspaceGrid,
    BedspaceButton,
    PaymentSection,
    PaymentOption,
    ConfirmButton,
    EmptyState,
    SectionTitle
} from "./HostelStyles";

const HostelBooking = () => {
    const queryClient = useQueryClient();

    const [selectedHostel, setSelectedHostel] = useState(null);
    const [selectedBedspace, setSelectedBedspace] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [showModal, setShowModal] = useState(false);

    // Fetch wallet balance
    const { data: walletData } = useQuery({
        queryKey: ["walletBalance"],
        queryFn: async () => {
            try {
                const res = await axiosget("ms_student_wallet_transactions/balance");
                return res?.data || { amount: 0 };
            } catch (e) {
                return { amount: 0 };
            }
        }
    });
    const walletBalance = walletData?.amount || 0;

    // Fetch current booking
    const { data: myBooking, isLoading: bookingLoading } = useQuery({
        queryKey: ["myHostelBooking"],
        queryFn: async () => {
            try {
                const res = await axiosget("ms_hostel_booking/my-booking");
                if (res?.data?.type === "error") return null;
                return res?.data || null;
            } catch (e) {
                return null;
            }
        }
    });

    // Fetch hostels
    const { data: hostels, isLoading: hostelsLoading, error } = useQuery({
        queryKey: ["availableHostels"],
        queryFn: async () => {
            try {
                const res = await axiosget("ms_hostel_booking/hostels");
                if (res?.data?.type === "error") {
                    throw new Error(res.data.message);
                }
                return res?.data || [];
            } catch (e) {
                throw new Error(e.message || "Failed to load hostels");
            }
        }
    });

    // Fetch rooms when hostel selected
    const { data: rooms, isLoading: roomsLoading } = useQuery({
        queryKey: ["hostelRooms", selectedHostel?._id],
        queryFn: async () => {
            const res = await axiosget(`ms_hostel_booking/hostels/${selectedHostel._id}/rooms`);
            return res.data;
        },
        enabled: !!selectedHostel
    });

    const handleSelectHostel = (hostel) => {
        setSelectedHostel(hostel);
        setSelectedBedspace(null);
        setPaymentMethod(null);
        setShowModal(true);
    };

    const handleSelectBedspace = (bedspace) => {
        if (bedspace.status === "Available") {
            setSelectedBedspace(bedspace);
        }
    };

    const handleBook = async () => {
        if (!selectedBedspace) {
            swal("error", "Please select a bedspace");
            return;
        }

        setProcessing(true);
        try {
            // First book the bedspace
            const bookRes = await axiospost("ms_hostel_booking/book", {
                bedspaceId: selectedBedspace._id
            });

            if (bookRes.data.type === "error") {
                swal("error", bookRes.data.message);
                setProcessing(false);
                return;
            }

            // Then initiate payment
            const payRes = await axiospost("ms_hostel_booking/pay", {
                allocationId: bookRes.data.allocation._id,
                paymentMethod
            });

            if (payRes.data.type === "success") {
                if (paymentMethod === "wallet") {
                    swal("success", payRes.data.message);
                    queryClient.invalidateQueries(["myHostelBooking"]);
                    queryClient.invalidateQueries(["availableHostels"]);
                    setShowModal(false);
                } else {
                    // Redirect to payment gateway
                    window.open(payRes.data.paymentUrl, "_blank");
                    swal("info", "Complete payment in the new tab, then click Verify");
                }
            } else {
                swal("error", payRes.data.message);
            }
        } catch (e) {
            swal("error", "An error occurred");
        }
        setProcessing(false);
    };

    const isLoading = bookingLoading || hostelsLoading;

    // Check if student has paid booking
    const hasPaidBooking = myBooking && myBooking.paymentStatus === "Paid";

    return (
        <HostelContainer>
            <PageTitle
                title="Hostel Accommodation"
                sub="Book your hostel accommodation for this academic session"
                icon="🏠"
            />

            {isLoading && <Loader />}
            {error && <ErrorResponse type="danger" message={error.message} />}

            {/* Current Booking */}
            {hasPaidBooking && (
                <CurrentBookingCard>
                    <BookingHeader>
                        <h3>Your Current Accommodation</h3>
                        <span className="status">✓ Confirmed</span>
                    </BookingHeader>
                    <BookingDetails>
                        <div className="detail">
                            <label>Hostel</label>
                            <span>{myBooking.bedSpace?.room?.hostel?.name}</span>
                        </div>
                        <div className="detail">
                            <label>Room</label>
                            <span>{myBooking.bedSpace?.room?.roomNumber}</span>
                        </div>
                        <div className="detail">
                            <label>Bed</label>
                            <span>{myBooking.bedSpace?.bedNumber}</span>
                        </div>
                        <div className="detail">
                            <label>Session</label>
                            <span>{myBooking.session?.name}</span>
                        </div>
                        <div className="detail">
                            <label>Amount Paid</label>
                            <span>{is_money(myBooking.amountPaid)}</span>
                        </div>
                    </BookingDetails>
                </CurrentBookingCard>
            )}

            {/* Hostels Grid */}
            {!hasPaidBooking && !isLoading && (
                <>
                    <SectionTitle>Available Hostels</SectionTitle>

                    {hostels?.type === "error" && (
                        <ErrorResponse type="danger" message={hostels.message} />
                    )}

                    {Array.isArray(hostels) && hostels.length === 0 && (
                        <EmptyState>
                            <i className="fa fa-building"></i>
                            <h3>No Hostels Available</h3>
                            <p>There are no hostels available for your gender at this time.</p>
                        </EmptyState>
                    )}

                    {Array.isArray(hostels) && hostels.length > 0 && (
                        <HostelsGrid>
                            {hostels.map((hostel) => (
                                <HostelCard key={hostel._id}>
                                    <HostelImage gender={hostel.genderType}>
                                        <i className="fa fa-building"></i>
                                    </HostelImage>
                                    <HostelInfo>
                                        <h3>{hostel.name}</h3>
                                        <HostelMeta>
                                            <span>
                                                <i className="fa fa-venus-mars"></i>
                                                {hostel.genderType}
                                            </span>
                                            <span>
                                                <i className="fa fa-door-open"></i>
                                                {hostel.totalRooms} Rooms
                                            </span>
                                            <span>
                                                <i className="fa fa-bed"></i>
                                                {hostel.availableBedspaces} Available
                                            </span>
                                        </HostelMeta>
                                        <HostelPrice>
                                            <div>
                                                <span className="price">{is_money(hostel.pricePerSession)}</span>
                                                <span className="per">/session</span>
                                            </div>
                                            <SelectButton
                                                onClick={() => handleSelectHostel(hostel)}
                                                disabled={hostel.availableBedspaces === 0}
                                            >
                                                {hostel.availableBedspaces === 0 ? "Full" : "Select"}
                                            </SelectButton>
                                        </HostelPrice>
                                    </HostelInfo>
                                </HostelCard>
                            ))}
                        </HostelsGrid>
                    )}
                </>
            )}

            {/* Room Selection Modal */}
            {showModal && selectedHostel && (
                <ModalOverlay onClick={() => setShowModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <h2>{selectedHostel.name} - Select Room & Bed</h2>
                            <button onClick={() => setShowModal(false)}>&times;</button>
                        </ModalHeader>
                        <ModalBody>
                            {roomsLoading && <Loader />}

                            {rooms && rooms.length === 0 && (
                                <EmptyState>
                                    <i className="fa fa-door-closed"></i>
                                    <p>No rooms available in this hostel</p>
                                </EmptyState>
                            )}

                            {rooms && rooms.map((room) => (
                                <RoomCard key={room._id}>
                                    <h4>Room {room.roomNumber} (Capacity: {room.capacity})</h4>
                                    <BedspaceGrid>
                                        {room.bedspaces.map((bed) => (
                                            <BedspaceButton
                                                key={bed._id}
                                                available={bed.status === "Available"}
                                                selected={selectedBedspace?._id === bed._id}
                                                onClick={() => handleSelectBedspace(bed)}
                                            >
                                                Bed {bed.bedNumber}
                                                <br />
                                                <small>{bed.status}</small>
                                            </BedspaceButton>
                                        ))}
                                    </BedspaceGrid>
                                </RoomCard>
                            ))}

                            {/* Payment Section */}
                            {selectedBedspace && (
                                <PaymentSection>
                                    <h3>Payment Method</h3>
                                    <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '16px' }}>
                                        Amount: <strong>{is_money(selectedHostel.pricePerSession)}</strong>
                                    </p>

                                    <PaymentOption
                                        selected={paymentMethod === "wallet"}
                                        onClick={() => setPaymentMethod("wallet")}
                                        iconBg="#48bb78"
                                    >
                                        <div className="icon">
                                            <i className="fa fa-wallet"></i>
                                        </div>
                                        <div className="info">
                                            <h4>Pay from Wallet</h4>
                                            <p>Balance: {is_money(walletBalance)}</p>
                                        </div>
                                    </PaymentOption>

                                    <PaymentOption
                                        selected={paymentMethod === "gateway"}
                                        onClick={() => setPaymentMethod("gateway")}
                                        iconBg="#667eea"
                                    >
                                        <div className="icon">
                                            <i className="fa fa-credit-card"></i>
                                        </div>
                                        <div className="info">
                                            <h4>Pay Online</h4>
                                            <p>Card, Bank Transfer, USSD</p>
                                        </div>
                                    </PaymentOption>

                                    <ConfirmButton
                                        onClick={handleBook}
                                        disabled={!paymentMethod || processing}
                                    >
                                        {processing ? "Processing..." : `Pay ${is_money(selectedHostel.pricePerSession)}`}
                                    </ConfirmButton>
                                </PaymentSection>
                            )}
                        </ModalBody>
                    </ModalContent>
                </ModalOverlay>
            )}
        </HostelContainer>
    );
};

export default HostelBooking;
